import { materialShapes } from 'unitData/Crushers/CrusherVSI/CrusherVSIData';
import type { UnitInterface } from 'store/units/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import { UnitTypes } from 'unitData/types';
import * as CM from 'unitData/CircularMotionScreen/CircularMotionScreenData';
import * as LM from 'unitData/LinearMotionScreen/LinearMotionScreenData';
import * as MS from 'unitData/MultislopeScreen/MultislopeScreenData';
import {
  screeningElements as SF,
  materials as SFMaterials,
} from 'unitData/FreeFallScreenSF/freeFallScreenSFData';
import {
  screeningElements as SS,
  materials as SSMaterials,
} from 'unitData/FreeFallScreenSS/freeFallScreenSSData';
import type { EquipmentOption } from './optionResolution';

export interface UnitOptionBinding {
  path: string[];
  keyPath?: string[];
  options: EquipmentOption[] | undefined;
  mode?: 'label' | 'object' | 'scalar';
}

export const readField = (
  source: unknown,
  path: readonly string[],
): unknown => {
  let value = source;
  for (const key of path) {
    if (!value || typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
};

export const readOptions = (source: unknown): EquipmentOption[] | undefined => {
  if (!Array.isArray(source)) return undefined;
  return source.filter((option: unknown): option is EquipmentOption => {
    if (!option || typeof option !== 'object') return false;
    return (
      ('value' in option &&
        (typeof option.value === 'string' ||
          typeof option.value === 'number') &&
        (('name' in option && typeof option.name === 'string') ||
          ('displayName' in option &&
            typeof option.displayName === 'string'))) ||
      ('model' in option && typeof option.model === 'string') ||
      ('simulationModel' in option &&
        typeof option.simulationModel === 'string' &&
        'displayName' in option &&
        typeof option.displayName === 'string') ||
      ('configKey' in option &&
        typeof option.configKey === 'number' &&
        'displayName' in option &&
        typeof option.displayName === 'string') ||
      ('id' in option &&
        (typeof option.id === 'string' || typeof option.id === 'number') &&
        (('name' in option && typeof option.name === 'string') ||
          ('displayName' in option && typeof option.displayName === 'string')))
    );
  });
};

const readOptionCollection = (source: unknown): EquipmentOption[] | undefined =>
  readOptions(
    source && typeof source === 'object' && !Array.isArray(source)
      ? Object.values(source)
      : source,
  );

const scalarOption = (
  value: unknown,
  label: unknown,
): EquipmentOption[] | undefined =>
  typeof value === 'string' && typeof label === 'string'
    ? [{ value, name: label }]
    : undefined;

/** A unit's variant and deck determine the catalog scope, never its saved label. */
export const mobileCatalogTypes: Partial<
  Record<UnitTypes, keyof UnitInfoDataInterface>
> = {
  [UnitTypes.MOBILE_TRACKED_JAW]: UnitTypes.MOBILE_JAW_COMMON,
  [UnitTypes.MOBILE_WHEELED_JAW]: UnitTypes.MOBILE_JAW_COMMON,
  [UnitTypes.MOBILE_TRACKED_CONE]: UnitTypes.MOBILE_CONE_COMMON,
  [UnitTypes.MOBILE_WHEELED_CONE]: UnitTypes.MOBILE_CONE_COMMON,
  [UnitTypes.MOBILE_TRACKED_SCREEN]: UnitTypes.MOBILE_SCREEN_COMMON,
  [UnitTypes.MOBILE_WHEELED_SCREEN]: UnitTypes.MOBILE_SCREEN_COMMON,
  [UnitTypes.MOBILE_TRACKED_HSI]: UnitTypes.MOBILE_HSI_COMMON,
  [UnitTypes.MOBILE_WHEELED_HSI]: UnitTypes.MOBILE_HSI_COMMON,
  [UnitTypes.MOBILE_VSI]: UnitTypes.MOBILE_VSI,
  [UnitTypes.MOBILE_JAW_AND_CONE]: UnitTypes.MOBILE_JAW_AND_CONE,
};

export const getUnitOptionBindings = (
  unit: UnitInterface,
  data: UnitInfoDataInterface,
  screenCalcMethod = 'NORMAL',
): UnitOptionBinding[] => {
  const bindings: UnitOptionBinding[] = [];
  const info = unit.unitInfo;
  const bind = (
    path: string[],
    options: unknown,
    mode: UnitOptionBinding['mode'] = 'label',
    keyPath?: string[],
  ) => {
    if (readField(info, path) !== undefined)
      bindings.push({ path, keyPath, options: readOptions(options), mode });
  };
  const decks = (
    path: string[],
    visit: (path: string[], index: string) => void,
  ) => {
    const value = readField(info, path);
    if (value && typeof value === 'object')
      Object.keys(value).forEach((index) => visit([...path, index], index));
  };
  const bindDeck = (
    path: string[],
    config: {
      elements?: unknown;
      shapes?: unknown;
      efficiency?: unknown;
      demand?: unknown;
      application?: unknown;
      media?: unknown;
    },
  ) => {
    bind([...path, 'screeningElement'], config.elements);
    bind([...path, 'particleShape'], config.shapes);
    bind([...path, 'efficiency'], config.efficiency);
    if (config.demand) {
      bind([...path, 'maxAmountOfOverUnderSize'], config.demand);
      bind([...path, 'maxAmountOfUnderSize'], config.demand);
    }
    bind([...path, 'typeOfApplication'], config.application);
    bind([...path, 'screeningMedia'], config.media, 'object');
  };
  if (unit.type === UnitTypes.FEED) {
    bind(
      ['inputFlow', 'materialName'],
      data.FEED?.predefinedMaterials,
      'scalar',
      ['inputFlow', 'materialId'],
    );
    bind(['inputFlow', 'psdName'], data.FEED?.psdCurves, 'scalar', [
      'inputFlow',
      'psdId',
    ]);
    return bindings;
  }

  const mobileType = mobileCatalogTypes[unit.type];
  if (mobileType) {
    const model = readField(info, ['modelName']);
    const catalog = readField(data[mobileType], [String(model)]);
    bind(
      ['configuration'],
      readField(catalog, ['configurations', 'configurationOptions']),
    );
    if (
      unit.type === UnitTypes.MOBILE_TRACKED_CONE ||
      unit.type === UnitTypes.MOBILE_WHEELED_CONE
    ) {
      bind(
        ['feederScreen', 'screenModel'],
        readField(catalog, ['configurations', 'FREEFALL_SCREEN1', 'models']),
        'scalar',
      );
    } else if (
      unit.type === UnitTypes.MOBILE_TRACKED_HSI ||
      unit.type === UnitTypes.MOBILE_WHEELED_HSI
    ) {
      const options = readField(catalog, ['configurations', 'HSI1', 'models']);
      bind(['HSI1', 'modelName'], readOptionCollection(options), 'scalar');
    } else if (unit.type === UnitTypes.MOBILE_VSI) {
      const options = readField(catalog, ['configurations', 'VSI1', 'model']);
      bind(['VSI1', 'modelName'], readOptionCollection(options), 'scalar');
      const conventionalScreenModel = readField(catalog, [
        'configurations',
        'CONVENTIONAL_SCREEN1',
        'model',
      ]);
      bind(
        ['SCREEN1', 'screenModel'],
        conventionalScreenModel ? [conventionalScreenModel] : undefined,
        'scalar',
      );
      const freeFallScreenModel = readField(catalog, [
        'configurations',
        'FREEFALL_SCREEN1',
        'model',
      ]);
      bind(
        ['FREEFALL_SCREEN1', 'modelName'],
        freeFallScreenModel ? [freeFallScreenModel] : undefined,
        'scalar',
      );
    } else if (unit.type === UnitTypes.MOBILE_JAW_AND_CONE) {
      for (const [component, keyField, labelField] of [
        ['JAW1', 'jawCrusherModel', 'jawCrusherModelName'],
        ['CONE1', 'coneCrusherModel', 'coneCrusherModelName'],
        ['GRIZZLY_FEEDER1', 'grizzlyModel', 'grizzlyModelName'],
      ]) {
        bind(
          [component, 'modelName'],
          scalarOption(
            readField(catalog, [keyField]),
            readField(catalog, ['configurations', labelField]),
          ),
          'scalar',
        );
      }
      return bindings;
    }
    for (const component of [
      'JAW1',
      'GRIZZLY_FEEDER1',
      'GRIZZLY_FEEDER2',
      'FEEDER_SCREEN1',
    ]) {
      const modelOption = readField(catalog, [
        'configurations',
        component,
        'model',
      ]);
      bind([component, 'modelName'], modelOption ? [modelOption] : undefined);
    }
    if (
      unit.type === UnitTypes.MOBILE_TRACKED_JAW ||
      unit.type === UnitTypes.MOBILE_WHEELED_JAW
    ) {
      bind(
        ['GRIZZLY_FEEDER1', 'decks', '0', 'grizzlyType'],
        readField(catalog, [
          'configurations',
          'GRIZZLY_FEEDER1',
          'decks',
          '0',
          'grizzlyTypeOptions',
        ]),
        'scalar',
      );
      bind(
        ['FEEDER_SCREEN1', 'decks', '0', 'screeningMedia'],
        readField(catalog, [
          'configurations',
          'FEEDER_SCREEN1',
          'decks',
          '0',
          'grizzlyTypeOptions',
        ]),
        'scalar',
      );
      bind(
        ['FEEDER_SCREEN1', 'decks', '1', 'screeningMedia'],
        readField(catalog, [
          'configurations',
          'FEEDER_SCREEN1',
          'decks',
          '1',
          'screeningMediaOptions',
        ]),
        'scalar',
      );
    }
    // Mobile screen and cone catalogs expose deck media keyed by simulation ID.
    bind(['VSI1', 'materialShape'], materialShapes);
    for (const component of [
      ['CONVENTIONAL_SCREEN1'],
      ['SCREEN1'],
      ['wheeledScreenUK', 'CONVENTIONAL_SCREEN1'],
    ]) {
      bind(
        [...component, 'materialType'],
        readField(catalog, ['rockMaterials']),
      );
      decks([...component, 'decks'], (path, index) => {
        bind([...path, 'overUnderSize'], readField(catalog, ['overUnderSize']));
        const media = readField(catalog, ['deckDetails', index]);
        const options =
          media && typeof media === 'object'
            ? Object.entries(media).map(([value, item]) => ({
                value,
                name: readField(item, ['displayName']),
              }))
            : undefined;
        bind([...path, 'screeningMediaId'], options);
      });
    }
    return bindings;
  }

  if (
    unit.type === UnitTypes.CONVENTIONAL_SCREEN ||
    unit.type === UnitTypes.LINEAR_MOTION_SCREEN ||
    unit.type === UnitTypes.MULTISLOPE_SCREEN
  ) {
    const config =
      unit.type === UnitTypes.CONVENTIONAL_SCREEN
        ? CM
        : unit.type === UnitTypes.LINEAR_MOTION_SCREEN
          ? LM
          : MS;
    bind(['materialType'], config.MaterialTypes);
    decks(['deckList'], (path) =>
      bindDeck(path, {
        shapes: config.AvailableParticleShapes,
        efficiency: config.AvailableEfficiency,
        media: config.getScreeningMediaData(
          String(readField(info, ['modelName']) ?? ''),
          Number(path[path.length - 1]),
        ),
        ...(unit.type === UnitTypes.MULTISLOPE_SCREEN
          ? { application: MS.AvailableTypeOfApplications }
          : {
              elements:
                unit.type === UnitTypes.CONVENTIONAL_SCREEN
                  ? CM.ScreeningMediaEfficiencyCalc
                  : LM.ScreeningMediaEfficiencyCalc,
              demand:
                unit.type === UnitTypes.CONVENTIONAL_SCREEN
                  ? CM.OverUnderSizeConstants
                  : LM.OverUnderSizeConstants,
            }),
      }),
    );
  } else if (
    unit.type === UnitTypes.FREEFALL_SCREEN_SF ||
    unit.type === UnitTypes.FREEFALL_SCREEN_SS
  ) {
    bind(
      ['materialType'],
      unit.type === UnitTypes.FREEFALL_SCREEN_SF ? SFMaterials : SSMaterials,
    );
    decks(['deckList'], (path, index) =>
      bind(
        [...path, 'screeningMediaId'],
        unit.type === UnitTypes.FREEFALL_SCREEN_SF
          ? SF
          : readField(SS, [String(Number(index) + 1)]),
      ),
    );
  } else if (
    [
      UnitTypes.BANANA_SCREEN,
      UnitTypes.KWATANI_SCREEN,
      UnitTypes.SCHENCK_SCREEN,
    ].includes(unit.type)
  ) {
    const config = readField(data, [unit.type, 'configurations']);
    bind(['materialType'], readField(config, ['rockMaterials']));
    decks(['deckList'], (path) =>
      bindDeck(path, {
        elements: readField(config, ['screeningMediaIds']),
        shapes: readField(config, ['particleShapes']),
        efficiency: readField(config, ['efficiencyParams']),
        media:
          unit.type === UnitTypes.KWATANI_SCREEN
            ? LM.ScreeningMediaDataNormalCalcKL
            : undefined,
      }),
    );
  } else if (unit.type === UnitTypes.GRIZZLY_SCREEN) {
    const config = data.GRIZZLY_SCREEN?.configurations;
    bind(['materialType'], config?.rockMaterials);
    decks(['deckList'], (path) =>
      bindDeck(path, {
        elements:
          screenCalcMethod === 'NORMAL'
            ? config?.normal.screeningMediaIds
            : config?.efficiency.screeningMediaIds,
        shapes: config?.efficiency.particleShapes,
        efficiency: config?.efficiency.efficiencyParameters,
        demand: config?.normal.overUnderSize,
      }),
    );
  } else if (unit.type === UnitTypes.IMPACT_CRUSHER_CV) {
    bind(['materialShape'], materialShapes);
  }
  return bindings;
};
